import React from 'react';
import {classes, style} from './doppe-action-type-blog-post-widget.st.css';
import {
    doppeHideableValueCreateHidden,
    doppeHideableValueIsVisible,
    doppeHideableValueModifyValue,
    doppeHideableValueToggleHidden
} from '../../../client-server-common/types/doppe-hideable-value';
import {DoppeActionTypeBlogPost} from '../doppe-action-type-blog-post';
import {DoppeActionGoalType, DoppeActionType} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {
    bymoPageContextPropsResolveBusinessEntity,
    IDoppeViewerActionContext,
    useBymoPageContextProps
} from '../../../doppe-sdk';
import {
    DoppeActionUnavailablePreview,
    DoppeActionUnavailableReason
} from '../../../components/doppe-action-unavailable-preview/doppe-action-unavailable-preview';
import {
    DoppeActionTypeWidgetInnerDescription
} from '../../../components/doppe-action-type-widget-inner-description/doppe-action-type-widget-inner-description';
import {
    DoppeActionTypeWidgetInnerTitle
} from '../../../components/doppe-action-type-widget-inner-title/doppe-action-type-widget-inner-title';
import {
    DoppeActionTypeWidgetContentDate
} from '../../../components/doppe-action-type-widget-content-date/doppe-action-type-widget-content-date';
import {
    DoppeActionTypeWidgetContentUrl
} from '../../../components/doppe-action-type-widget-content-url/doppe-action-type-widget-content-url';
import {
    dateSerializedToString,
    stringIsNotNullableOrWhiteSpace,
    stringIsNullableOrWhiteSpace
} from '@wix/devzai-utils-common';
import {
    doppeBusinessEntityDtoIsNotFound,
    doppeExternalBlogPostProviderResolveBusinessEntityReference
} from '../../../client-server-common';
import {DoppeDtoBlogPost} from '../../../client-server-common/types/doppe-dto-blog-post';
import {
    DoppeActionTypeWidgetLayout
} from '../../../components/doppe-action-type-widget-layout/doppe-action-type-widget-layout';
import {useDoppeViewerLocales} from '../../../client/doppe-viewer-locales/doppe-viewer-locales';
import {
    DoppeActionTypeWidgetContentLayoutWrapper2
} from '../../../components/doppe-action-type-widget-content-layout-wrapper-2/doppe-action-type-widget-content-layout-wrapper-2';

export const DoppeActionTypeBlogPostWidget = React.memo(function DoppeActionTypeBlogPostWidget(
    props: DoppeActionType.WidgetProps<DoppeActionTypeBlogPost.ActionSettings>
) {
    const {
        actionContext,

        className,
        ...htmlAttributes
    } = props;

    return (
        <DoppeActionTypeWidgetLayout
            {...htmlAttributes}
            className={style(classes.root, {}, className)}
            actionContext={actionContext}
        >
            <DoppeActionTypeBlogPostBindableWidget
                reportGoalAchieved={() => {
                    actionContext.reportGoalAchieved({
                        goalType: DoppeActionGoalType.Click,
                    })
                }}
                actionId={actionContext.action.id}
                actionSettings={actionContext.action.settings}
                actionContext={actionContext}
            />
        </DoppeActionTypeWidgetLayout>
    );
});

export const DoppeActionTypeBlogPostBindableWidget = React.memo(function DoppeActionTypeBlogPostBindableWidget(
    props: {
        actionSettings: DoppeActionTypeBlogPost.ActionSettings;
        reportGoalAchieved: (goalType: DoppeActionGoalType) => void;
        actionId: string;
        actionContext: IDoppeViewerActionContext;
        listItemId?: string;
    }
) {
    const {
        actionSettings,
        reportGoalAchieved,
        actionId,
        actionContext,
        listItemId
    } = props;

    const bymoPageContextProps = useBymoPageContextProps();
    const {
        blogPostProvider,
        blogPostProviderId
    } = actionSettings;

    if (blogPostProvider !== null && blogPostProviderId !== null) {
        const boundBlogPost = bymoPageContextPropsResolveBusinessEntity(
            bymoPageContextProps,
            doppeExternalBlogPostProviderResolveBusinessEntityReference(blogPostProvider, blogPostProviderId))

        if (!boundBlogPost || doppeBusinessEntityDtoIsNotFound(boundBlogPost)) {
            return (
                <DoppeActionTypeBlogPostWidgetContentUnavailable
                    reason={
                        !boundBlogPost ?
                            DoppeActionUnavailableReason.ContentLoadingError :
                            DoppeActionUnavailableReason.ContentUnavailable
                    }
                />
            )
        } else {
            return (
                <DoppeActionTypeBlogPostWidgetInner
                    reportGoalAchieved={reportGoalAchieved}
                    actionId={actionId}
                    actionSettings={{
                        ...actionSettings,
                        ...doppeActionTypeBlogPostWidgetResolveSettingsFromBoundEntity(actionSettings, boundBlogPost)
                    }}
                    actionContext={actionContext}
                    listItemId={listItemId}
                />
            )
        }
    } else {
        return (
            <DoppeActionTypeBlogPostWidgetInner
                reportGoalAchieved={reportGoalAchieved}
                actionId={actionId}
                actionSettings={actionSettings}
                actionContext={actionContext}
                listItemId={listItemId}
            />
        )
    }
});

export const DoppeActionTypeBlogPostWidgetContentUnavailable = React.memo(function DoppeActionTypeBlogPostWidgetContentUnavailable(
    props: {
        reason: DoppeActionUnavailableReason;
    }
) {
    const {
        reason
    } = props;

    const doppeViewerLocales = useDoppeViewerLocales();

    return (
        <DoppeActionUnavailablePreview
            reason={reason}
            title={doppeViewerLocales['contentUnavailableTitleBlog']}
        />
    )
});

export const DoppeActionTypeBlogPostWidgetInner = React.memo(function DoppeActionTypeBlogPostWidgetInner(
    props: {
        actionSettings: DoppeActionTypeBlogPost.ActionSettings;
        reportGoalAchieved: (goalType: DoppeActionGoalType) => void;
        actionId: string;
        actionContext: IDoppeViewerActionContext;
        listItemId?: string;
    }
) {
    const {
        actionSettings,
        reportGoalAchieved,
        actionContext,
        listItemId
    } = props;

    const {
        blogPostLink,
        description,
        blogPostTitle,
        blogPostDate,
        blogPostImages,
        showBlogWebsite,
        mainMediaViewLayout,
        mainMediaViewSettings

    } = actionSettings;

    const hideableBlogPostLink =
        doppeHideableValueToggleHidden(blogPostLink, stringIsNullableOrWhiteSpace(blogPostLink) || !showBlogWebsite);

   return (
        <DoppeActionTypeWidgetContentLayoutWrapper2
            actionContext={actionContext}
            reportGoalAchieved={reportGoalAchieved}
            mainMediaViewLayout={mainMediaViewLayout}
            mainMediaViewSettings={mainMediaViewSettings}
            media={blogPostImages}
            sections={actionSettings.sections}
            buttons={actionSettings.ctaButtons}
            buttonsSettings={{
                sourceURL: blogPostLink,
                listItemId: listItemId,
            }}
        >
            <DoppeActionTypeWidgetInnerTitle>
                {blogPostTitle}
            </DoppeActionTypeWidgetInnerTitle>
            {doppeHideableValueIsVisible(blogPostDate) || doppeHideableValueIsVisible(hideableBlogPostLink) ? (
                <div>
                    <DoppeActionTypeWidgetContentDate>{blogPostDate}</DoppeActionTypeWidgetContentDate>
                    <DoppeActionTypeWidgetContentUrl>{hideableBlogPostLink}</DoppeActionTypeWidgetContentUrl>
                </div>
            ) : null}

            <DoppeActionTypeWidgetInnerDescription>
                {description}
            </DoppeActionTypeWidgetInnerDescription>
        </DoppeActionTypeWidgetContentLayoutWrapper2>
    );
});

export function doppeActionTypeBlogPostWidgetResolveSettingsFromBoundEntity(
    actionSettings: DoppeActionTypeBlogPost.ArticleSettings,
    doppeDtoBlogPost: DoppeDtoBlogPost
): DoppeActionTypeBlogPost.ArticleSettings {
    return {
        ...actionSettings,
        blogPostDate: doppeDtoBlogPost.firstPublishedDate ?
            doppeHideableValueModifyValue(actionSettings.blogPostDate, dateSerializedToString(doppeDtoBlogPost.firstPublishedDate)) :
            doppeHideableValueCreateHidden(dateSerializedToString(new Date(0))),
        showBlogWebsite: stringIsNotNullableOrWhiteSpace(doppeDtoBlogPost.blogPostPageUrl) ?
            actionSettings.showBlogWebsite : false,
        blogPostLink: stringIsNotNullableOrWhiteSpace(doppeDtoBlogPost.blogPostPageUrl) ?
            doppeDtoBlogPost.blogPostPageUrl : 'about:blank',
    }
}